import { useAppStore } from 'hooks/state/useAppStore';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { Attribute, Measure, useModelService } from 'services/model/model.service';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { ExpandableRow } from 'components/table/ExpandableRow';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { createExpanderCell, createMeasureCell, sortComparisonCell } from 'components/table/DataTableUtil';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { IntlShape, useIntl } from 'react-intl';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { DataTableExportConfigOptions } from 'components/table/DataTableExportUtil';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';

interface GmvCategoryBreakdownRow extends ExpandableRow<GmvCategoryBreakdownRow> {
  category: string;
  categoryCode?: string;
  share: number;
  ordGmv: number;
  ordGmvCompare: number;
  ordNum: number;
  ordNumCompare: number;
  itemNetSlsGmv: number;
  itemNetSlsGmvCompare: number;
  retRate: number;
  retRateCompare: number;
  netSlsGmv: number;
  netSlsGmvCompare: number;
}

const columnHelper = createColumnHelper<GmvCategoryBreakdownRow>();
const buildColumns = (prdCategory: Attribute, measures: Measure[], intl: IntlShape, comparisonActive: boolean) => [
  columnHelper.accessor('category', {
    header: () => prdCategory.name,
    cell: context => {
      const row = context.row;
      const categoryCode = row.original.categoryCode;
      const icon = categoryCode ? findIconForCategory(categoryCode, 20) : null;
      return createExpanderCell(
        context,
        <div className="flex items-center gap-2">
          {icon && <div className="flex-shrink-0">{icon}</div>}
          <span>{context.getValue()}</span>
        </div>,
        true
      );
    },
  }),
  columnHelper.accessor('share', {
    header: () => intl.formatMessage({ id: 'insights.report.share' }),
    cell: context => (
      <ShareBar width={context.getValue() * 100} color={context.row.getCanExpand() ? '#314968' : '#5CBBC7'} />
    ),
  }),
  columnHelper.accessor(row => ({ value: row.ordGmv, compareValue: row.ordGmvCompare }), {
    id: measures[0].code,
    header: () => measures[0].metric.name,
    cell: context => createMeasureCell(context, measures[0], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.ordNum, compareValue: row.ordNumCompare }), {
    id: measures[1].code,
    header: () => measures[1].metric.name,
    cell: context => createMeasureCell(context, measures[1], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.itemNetSlsGmv, compareValue: row.itemNetSlsGmvCompare }), {
    id: measures[2].code,
    header: () => measures[2].metric.name,
    cell: context => createMeasureCell(context, measures[2], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.retRate, compareValue: row.retRateCompare }), {
    id: measures[3].code,
    header: () => measures[3].metric.name,
    cell: context => createMeasureCell(context, measures[3], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
  columnHelper.accessor(row => ({ value: row.netSlsGmv, compareValue: row.netSlsGmvCompare }), {
    id: measures[4].code,
    header: () => measures[4].metric.name,
    cell: context => createMeasureCell(context, measures[4], false, comparisonActive),
    sortingFn: sortComparisonCell,
  }),
];

export function GmvCategoryBreakdownTable() {
  const { products } = useAppStore();
  const { fetchData } = useProDataService(Service.Categories);
  const requestData = useDataRequest();
  const intl = useIntl();

  const { getMeasures, getAttributes } = useModelService();

  const [prdCategory, chnName] = getAttributes(['prdCategory', 'chnName']);

  const measures = getMeasures([
    'ordGmvVal',
    'ordQty',
    'itemNetSlsGmvValExpOD',
    'retQtyRateExpOD',
    'netSlsGmvValExpOD',
  ]);

  const [ordGmvVal, ordQty, itemNetSlsGmvVal, retQtyRate, netSlsGmvVal] = measures;
  const productNumbers = products?.map(p => p);

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData('reports/gmv-category', {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );

      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-categories-gmv-category-report']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const rows =
    reportState.data?.response.sets.categories.map(row => {
      const categoryRow: GmvCategoryBreakdownRow = {
        category: prdCategory.getValue(row.attributes),
        categoryCode: prdCategory.getRawValue(row.attributes),
        share:
          ordGmvVal.getValue(row.values) /
          (ordGmvVal.getValue(reportState.data!.response.values) ?? ordGmvVal.getValue(row.values)),
        ordGmv: ordGmvVal.getValue(row.values),
        ordGmvCompare: ordGmvVal.getValue(row.compareValues),
        ordNum: ordQty.getValue(row.values),
        ordNumCompare: ordQty.getValue(row.compareValues),
        itemNetSlsGmv: itemNetSlsGmvVal.getValue(row.values),
        itemNetSlsGmvCompare: itemNetSlsGmvVal.getValue(row.compareValues),
        retRate: retQtyRate.getValue(row.values),
        retRateCompare: retQtyRate.getValue(row.compareValues),
        netSlsGmv: netSlsGmvVal.getValue(row.values),
        netSlsGmvCompare: netSlsGmvVal.getValue(row.compareValues),
        subRows: [],
      };

      categoryRow.subRows = row.sets.channels.map(subRow => {
        const channelRow: GmvCategoryBreakdownRow = {
          category: chnName.getValue(subRow.attributes),
          share:
            ordGmvVal.getValue(subRow.values) / (ordGmvVal.getValue(row.values) ?? ordGmvVal.getValue(subRow.values)),
          ordGmv: ordGmvVal.getValue(subRow.values),
          ordGmvCompare: ordGmvVal.getValue(subRow.compareValues),
          ordNum: ordQty.getValue(subRow.values),
          ordNumCompare: ordQty.getValue(subRow.compareValues),
          itemNetSlsGmv: itemNetSlsGmvVal.getValue(subRow.values),
          itemNetSlsGmvCompare: itemNetSlsGmvVal.getValue(subRow.compareValues),
          retRate: retQtyRate.getValue(subRow.values),
          retRateCompare: retQtyRate.getValue(subRow.compareValues),
          netSlsGmv: netSlsGmvVal.getValue(subRow.values),
          netSlsGmvCompare: netSlsGmvVal.getValue(subRow.compareValues),
          subRows: [],
        };

        return channelRow;
      });

      return categoryRow;
    }) ?? [];
  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  const columns = buildColumns(prdCategory, measures, intl, isComparisonActive) as ColumnDef<GmvCategoryBreakdownRow>[];

  const exportConfigOptions: DataTableExportConfigOptions = {
    reportDownloadProps: {
      service: Service.Categories,
      measuresGroup: 'gmv-category',
      tableName: 'GMV Category Breakdown',
      isProReport: true,
    },
  };

  return (
    <DataTableContextProvider>
      <DataTable
        titleId="insights.category.reports.channel_breakdown.description"
        rows={rows}
        columns={columns}
        defaultSortingState={[{ id: measures[0].code, desc: true }]}
        exportConfigOptions={exportConfigOptions}
        isError={reportState.isError}
        errorMessage={reportState.error}
      />
    </DataTableContextProvider>
  );
}
